// levelpack.ts — the pack-level adapter: read a compiled mission (.lvl
// crunched-ini + its [General] TerrainMap .trmap) out of the SHIPPED pack at
// runtime and produce the LevelData vector the TS sim's Level loader consumes —
// the same shape sim/tools/extract-level.mjs writes offline (the test-vector
// pipeline), built from the same parser (sim/tools/vendor/cini.cjs).
//
//  - LoadIniFile ↔ mpshared/ini.cpp:256 (IniReader over a pack file)
//  - LevelDataFromPack ↔ the data slice of game/Level.cpp LoadLevelConstants
//    (:235-247: [General] TerrainMap -> TerrainMap::Init over the
//    u16BE ctx, u16BE cty, ctx*cty terrain-byte .trmap file)

import { IniReader, type IniSection } from '../../../sim/src/data/ini.ts';
import type { LevelData } from '../../../sim/src/level.ts';
import type { PackFileReader } from './packfile.ts';
import { parseCini } from '../../../sim/tools/vendor/cini.cjs';

// mpshared/ini.cpp:256-268 — IniReader *LoadIniFile(PackFileReader&, pszFn)
export function LoadIniFile(pakr: PackFileReader, pszFn: string): IniReader | null {
  const pb = pakr.MapFile(pszFn);
  if (pb === null) return null;
  const sections = parseCini(Buffer.from(pb)) as IniSection[];
  return new IniReader(sections);
}

/** The runtime twin of sim/tools/extract-level.mjs: .lvl sections RAW and
 *  ORDERED + the trmap bytes (sim/src/level.ts LevelData). */
export function LevelDataFromPack(pakr: PackFileReader, szLvlFilename: string): LevelData {
  const pbLvl = pakr.MapFile(szLvlFilename);
  if (pbLvl === null) throw new Error(`LevelDataFromPack: ${szLvlFilename} not in pack`);
  const sections = parseCini(Buffer.from(pbLvl)) as IniSection[];

  // game/Level.cpp:235-238 — the [General] TerrainMap property names the file
  const pini = new IniReader(sections);
  const szTrmap = pini.GetString('General', 'TerrainMap');
  if (szTrmap === null) throw new Error(`LevelDataFromPack: ${szLvlFilename} has no TerrainMap`);

  // game/terrainmap.cpp TerrainMap::Init — u16BE ctx, u16BE cty, terrain bytes
  const tr = pakr.MapFile(szTrmap);
  if (tr === null) throw new Error(`LevelDataFromPack: ${szTrmap} not in pack`);
  const ctx = (tr[0] << 8) | tr[1];
  const cty = (tr[2] << 8) | tr[3];
  if (tr.length < 4 + ctx * cty) {
    throw new Error(`LevelDataFromPack: trmap short: ${tr.length} < 4+${ctx * cty}`);
  }
  const terrain = Buffer.from(tr.subarray(4, 4 + ctx * cty)).toString('base64');

  return { sections, trmap: { ctx, cty, terrain } };
}
